// Structural types on purpose: importing DefectGroup from reportPage.component.tsx
// would pull Capacitor modules into jest.
export interface StatusDefect {
    id?: number | string;
    name?: string;
    other?: string;
    defect?: unknown;
}

export interface StatusDefectGroup {
    id?: number | string;
    quantity?: number | string;
    repaired?: number;
    notRepaired?: number;
    defects?: StatusDefect[];
}

export type SingleComponentStatus = 'ok' | 'nok' | 'reworked';

const toNumber = (value: number | string | undefined): number => {
    if (value === undefined || value === null || value === '') return 0;
    const parsed = typeof value === 'string' ? parseInt(value, 10) : value;
    return isNaN(parsed) ? 0 : parsed;
};

export const getSingleComponentStatus = (defectGroups?: StatusDefectGroup[]): SingleComponentStatus => {
    if (!defectGroups || defectGroups.length === 0) return 'ok';
    if (defectGroups.some((g) => toNumber(g.notRepaired) > 0)) return 'nok';
    if (defectGroups.some((g) => toNumber(g.repaired) > 0)) return 'reworked';
    if (defectGroups.some((g) => toNumber(g.quantity) > 0)) return 'nok';
    return 'ok';
};

export const applySingleComponentStatus = (
    defectGroups: StatusDefectGroup[],
    subType: string | undefined,
    status: Exclude<SingleComponentStatus, 'ok'>,
    initialDefects: StatusDefect[],
): StatusDefectGroup[] => {
    if (getSingleComponentStatus(defectGroups) === status) {
        return [];
    }

    const counts: Pick<StatusDefectGroup, 'quantity' | 'repaired' | 'notRepaired'> =
        subType === 'OPTIMIZATION'
            ? (status === 'nok' ? { repaired: 0, notRepaired: 1 } : { repaired: 1, notRepaired: 0 })
            : { quantity: 1 };

    if (defectGroups.length === 0) {
        return [{ ...counts, defects: initialDefects }];
    }

    return defectGroups.map((group, index) => (index === 0 ? { ...group, ...counts } : group));
};

export const getNokTotal = (group: StatusDefectGroup, subType?: string): number => {
    if (subType === 'OPTIMIZATION') {
        return toNumber(group.repaired) + toNumber(group.notRepaired);
    }
    return toNumber(group.quantity);
};

export const applyNokTotal = (
    group: StatusDefectGroup,
    subType: string | undefined,
    nokTotal: number,
): StatusDefectGroup => {
    const total = Math.max(0, nokTotal);
    if (subType === 'OPTIMIZATION') {
        const repaired = Math.min(toNumber(group.repaired), total);
        return { ...group, repaired: repaired, notRepaired: total - repaired };
    }
    return { ...group, quantity: total };
};

export const applyReworkedCount = (group: StatusDefectGroup, reworked: number): StatusDefectGroup => {
    const total = toNumber(group.repaired) + toNumber(group.notRepaired);
    const repaired = Math.min(Math.max(0, reworked), total);
    return { ...group, repaired: repaired, notRepaired: total - repaired };
};

export const getRowComponentsSummary = (
    components: Array<{ quantity?: number | string; defectGroups?: StatusDefectGroup[] }>,
    subType?: string,
): { total: number; ok: number; reworked: number; notReworked: number } => {
    let total = 0;
    let reworked = 0;
    let notReworked = 0;

    for (const component of components) {
        total += toNumber(component.quantity);
        for (const group of component.defectGroups ?? []) {
            if (subType === 'OPTIMIZATION') {
                reworked += toNumber(group.repaired);
                notReworked += toNumber(group.notRepaired);
            } else {
                notReworked += toNumber(group.quantity);
            }
        }
    }

    return { total, ok: Math.max(0, total - reworked - notReworked), reworked, notReworked };
};
